import { Children, PropsWithChildren, ReactElement, useRef } from 'react';

import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { If, Loading } from '@hsp/react-component-library';

import { DataTable, Expander, TableToolbar, ToolbarItems } from 'components/table/components';
import { Paginator } from 'components/table/components/paginator';
import { TableProvider } from 'components/table/context';
import { ITableProps } from 'types/components/table';

import './table.component.scss';

function Table<TData, TValue = unknown>({
  children,
  Filter,
  enablePaging = true,
  globalFilterPlaceHolder,
  loading = false,
  page = 1,
  pageSizeOptions,
  sorts,
  ...props
}: ITableProps<TData, TValue>) {
  const childrenArray = Children.toArray(children);
  const columnHelper = createColumnHelper<TData>();
  const ref = useRef<HTMLDivElement | null>(null);
  let toolbarItems = 0;

  if (props.enableExpanding) {
    props.columns = [
      columnHelper.display({
        cell: ({ row: { getCanExpand, getIsExpanded } }) => (
          <Expander
            canExpand={getCanExpand()}
            expanded={getIsExpanded()}
          />
        ),
        id: 'table-expander-cell',
        size: 40,
      }) as ColumnDef<TData, TValue>,
      ...props.columns,
    ];
  }

  childrenArray.forEach((child) => {
    const { type } = child as ReactElement<PropsWithChildren>;

    if (type !== ToolbarItems) {
      throw new Error('Table only supports ToolbarItems as children');
    }

    if (type === ToolbarItems) {
      toolbarItems += 1;
    }

    if (toolbarItems > 1) {
      throw new Error('Only 1 ToolbarItems is allowed');
    }
  });

  return (
    <TableProvider sorts={sorts}>
      <TableToolbar
        enableFilter={props.enableGlobalFilter}
        Filter={Filter}
        filterPlaceHolder={globalFilterPlaceHolder}>
        {childrenArray.find((child) => {
          const { type } = child as ReactElement<PropsWithChildren>;

          return type === ToolbarItems;
        })}
      </TableToolbar>
      <div
        className="table-wrapper"
        ref={ref}>
        <DataTable
          {...props}
          enablePaging={enablePaging}
          sorts={sorts}
        />
        <If condition={enablePaging}>
          <Paginator
            page={page}
            pageSizeOptions={pageSizeOptions}
          />
        </If>
      </div>
      <If condition={loading}>
        <Loading
          targetRef={ref}
          type="dual-ring"
        />
      </If>
    </TableProvider>
  );
}

export default Table;
